import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Anchor, Ship, AlertTriangle, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const BelgiumStrikesArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-orange-500 text-white">
                <Anchor className="h-3 w-3 mr-1" />
                {t('article.belgium.badge')}
              </Badge>
              <span className="text-gray-500 text-sm">{t('article.belgium.date')}</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.belgium.title')}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.belgium.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://imgs.newseditor.aws.kuehne-nagel.cloud/c5c3735c-bc18-48df-8f79-651810519cbf.jpg"
              alt="Belgium port strikes affecting Antwerp and Zeebrugge operations" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Ship className="h-5 w-5 mr-2 text-red-600" />
                  {t('article.belgium.stat1')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">{t('article.belgium.statValue1')}</div>
                <p className="text-sm text-gray-600">{t('article.belgium.statDesc1')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
                  {t('article.belgium.stat2')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">{t('article.belgium.statValue2')}</div>
                <p className="text-sm text-gray-600">{t('article.belgium.statDesc2')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-blue-600" />
                  {t('article.belgium.stat3')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{t('article.belgium.statValue3')}</div>
                <p className="text-sm text-gray-600">{t('article.belgium.statDesc3')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('article.belgium.heading1')}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.belgium.content1')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.belgium.heading2')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('article.belgium.operationsIntro')}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>{t('article.belgium.pilotageServices').split(':')[0]}:</strong> {t('article.belgium.pilotageServices').split(':')[1]}</li>
              <li><strong>{t('article.belgium.vesselTraffic').split(':')[0]}:</strong> {t('article.belgium.vesselTraffic').split(':')[1]}</li>
              <li><strong>{t('article.belgium.containerHandling').split(':')[0]}:</strong> {t('article.belgium.containerHandling').split(':')[1]}</li>
              <li><strong>{t('article.belgium.hinterlandConnections').split(':')[0]}:</strong> {t('article.belgium.hinterlandConnections').split(':')[1]}</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.belgium.portResponseTitle')}</h3>
            <div className="bg-orange-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('article.belgium.currentSituationTitle')}</h4>
              <ul className="space-y-2">
                <li><strong>{t('article.belgium.antwerpStatus').split(':')[0]}:</strong> {t('article.belgium.antwerpStatus').split(':')[1]}</li>
                <li><strong>{t('article.belgium.zeebruggeStatus').split(':')[0]}:</strong> {t('article.belgium.zeebruggeStatus').split(':')[1]}</li>
                <li><strong>{t('article.belgium.recoveryTimeline').split(':')[0]}:</strong> {t('article.belgium.recoveryTimeline').split(':')[1]}</li>
                <li><strong>{t('article.belgium.serviceRestoration').split(':')[0]}:</strong> {t('article.belgium.serviceRestoration').split(':')[1]}</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.belgium.transportationTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.belgium.transportationContent')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.belgium.carrierTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('article.belgium.carrierContent')}
            </p>
            <blockquote className="border-l-4 border-orange-500 pl-6 italic text-gray-700 mb-6">
              "{t('article.belgium.hapagQuote')}"
              <footer className="text-sm font-semibold mt-2">{t('article.belgium.hapagFooter')}</footer>
            </blockquote>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.belgium.congestionTitle')}</h3>
            <div className="bg-red-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('article.belgium.whyDelaysTitle')}</h4>
              <ul className="space-y-2">
                <li><strong>{t('article.belgium.vesselBacklog').split(':')[0]}:</strong> {t('article.belgium.vesselBacklog').split(':')[1]}</li>
                <li><strong>{t('article.belgium.serviceCatchup').split(':')[0]}:</strong> {t('article.belgium.serviceCatchup').split(':')[1]}</li>
                <li><strong>{t('article.belgium.containerAccumulation').split(':')[0]}:</strong> {t('article.belgium.containerAccumulation').split(':')[1]}</li>
                <li><strong>{t('article.belgium.schedulingDisruption').split(':')[0]}:</strong> {t('article.belgium.schedulingDisruption').split(':')[1]}</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Economic Context</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The strike emerged from union opposition to government austerity measures aimed at 
              reducing Belgium's budget deficit. Trade unions argued that the proposed policies 
              would disproportionately impact workers while failing to address structural economic 
              issues. This political dimension suggests potential for future disruptions if 
              negotiations fail to reach satisfactory agreements.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Supply Chain Implications</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The disruption at Antwerp-Bruges, Europe's second-largest port complex, has significant 
              implications for European supply chains. Companies relying on just-in-time delivery 
              face particular challenges, while the congestion may force some cargo to alternative 
              ports, creating ripple effects across the continental logistics network.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Recovery Outlook</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              While operations have technically resumed, port authorities and shipping lines expect 
              several days to clear the vessel backlog and normalize operations. The "significant" 
              congestion noted by Hapag-Lloyd reflects the complex logistics of managing 41 delayed 
              vessels while maintaining regular scheduled services.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">Source Information:</h4>
              <p className="text-sm text-gray-600">
                Based on reports from Kuehne+Nagel maritime news and port authority updates. 
                <a href="https://mykn.kuehne-nagel.com/news/article/strikes-continue-to-cause-congestion-at" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  View original source
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default BelgiumStrikesArticle;